(ns practitest.frontend.components2.dashboard.schema
  (:require [practitest.frontend.components2.dashboard.chart :as-alias chart]
            [malli.core :as m]
            [malli.util :as mu]
            [malli.registry :as mr]))

(defn- sorted-pred [{:keys [by order]}]
  (let [comp (if (= order :desc) >= <=)]
    #(let [values (seq (map by %))]
       (if values
         (apply comp values)
         true))))

;; Malli schema that validates a collection is sorted.
;; Options:
;;   :by    — keyword or fn to extract the sort key (optional, defaults to identity)
;;   :order — :asc or :desc
;; Usage:
;;   [:and
;;    [:vector [:map [:value number?]]]
;;    [:sorted {:by :value :order :desc}]]
(def Sorted
  (m/-simple-schema
   (fn [{:keys [by order] :as opts} _]
     (let [error-msg (str "must be sorted by " (name by) " " (name order))]
       {:type              :sorted
        :pred              (sorted-pred opts)
        :type-properties   {:error/message error-msg}
        :properties-schema [:map
                            [:by :keyword]
                            [:order [:enum :asc :desc]]]}))))

(mr/set-default-registry!
 (merge (m/default-schemas)
        (mu/schemas)
        {:sorted Sorted}))

(def ColorMap
  [:map-of [:string {:min 1}] [:string {:min 1}]])

(def ChartDataOrWarning
  [:fn
   (fn [{:chart/keys [warning data]}]
     (or (some? data)
         (some? warning)))])

(def PieData
  [:vector
   [:tuple :string number?]])

(def PlannedVsExecutedData
    [:map
     [:executed
      [:vector
       [:map
        [:date :string]
        [:instance-runned [:maybe :int]]]]]
     [:planned
      [:vector
       [:map
        [:date :string]
        [:instance-runned :int]]]]])

(def SeriesCategories
  [:map
   [:series [:vector
             [:map
              [:name :string]
              [:data [:vector :int]]]]]
   [:categories [:vector :string]]])

(def SeriesInstCategories
  [:map
   [:series [:vector
             [:map
              [:name :string]
              [:data [:vector :int]]]]]
   [:categories [:vector inst?]]])

(def Route
  [:map
   [:uri :string]
   [:ext [:map-of :keyword :string]]
   [:params [:vector [:tuple :string :string]]]])

(def Warning
  [:map
   [:type    [:enum
              :error-configuration
              :currently-unavailable
              :no-data
              :irrelevant-tab-filter
              :empty-data]]
   [:message :string]])

(def ChartItem
  [:map
   [:chart/id :int]
   [:chart/title :string]
   [:chart/sub-title [:maybe :string]]
   [:chart/entity-name :string]
   [:chart/item-route [:maybe Route]]
   [:chart/user-colors [:maybe ColorMap]]
   [:chart/percentage-mode :boolean]
   [:chart/remove-empty-values :boolean]
   [:chart/warning {:optional true} [:maybe Warning]]
   [:chart/cell-width {:optional true} [:maybe [:enum :regular :double]]]
   [:chart/cell-height {:optional true} [:maybe [:enum :regular :double]]]
   [:chart/legend-location {:optional true} [:maybe [:enum :bottom :no-legend]]]])

(defn- bar-chart-schema [chart-type]
  [:and
   [:merge ChartItem
    [:map [:chart/type [:= chart-type]]
     [:chart/data {:optional true} SeriesCategories]]]
   ChartDataOrWarning])

(def BarChart
  (bar-chart-schema ::chart/bar))

(def StackedBarChart
  (bar-chart-schema ::chart/stacked-bar))

(def AgeChart
  (bar-chart-schema ::chart/age))

(def StackedProgressBarChart
  [:and
   [:merge ChartItem
    [:map
     [:chart/type [:= ::chart/stacked-progress-bar]]
     [:chart/data {:optional true} SeriesInstCategories]]]
   ChartDataOrWarning])

(def PieDataItem
  [:map
   [:value number?]])

(def GaugeDataItem
  [:map
   [:value [:and number? [:>= 0] [:<= 1]]]
   [:criteria :string]
   [:thresholds
    [:map
     [:low number?]
     [:medium number?]]]])

(def TableData
  [:map
   [:columns [:vector [:map [:key :keyword] [:label :string]]]]
   [:rows [:vector [:map-of :keyword :any]]]
   [:table-type :string]])

(def ^:private PieChartBase
  [:merge
   ChartItem
   [:map
    [:chart/type [:= ::chart/pie]]
    [:chart/data {:optional true} PieData]]])

(def PieChart
  [:and PieChartBase ChartDataOrWarning])

(def PieChartTransformed
  [:merge
   PieChartBase
   [:map
    [:chart/data
     [:and
      [:vector PieDataItem]
      [:sorted {:by :value :order :desc}]]]]])

(def PlannedVsExecutedChart
  [:and
   [:merge
    ChartItem
    [:map
     [:chart/type [:= ::chart/line]]
     [:chart/data {:optional true} PlannedVsExecutedData]]]
   ChartDataOrWarning])

(def GaugeChart
  [:and
   [:merge
    ChartItem
    [:map
     [:chart/type [:= ::chart/gauge]]
     [:chart/data {:optional true} GaugeDataItem]]]
   ChartDataOrWarning])

(def TableChart
  [:and
   [:merge
   ChartItem
   [:map
    [:chart/type [:= ::chart/table]]
    [:chart/data {:optional true} TableData]]]
   ChartDataOrWarning])

(def StatusChangesChart
  [:and
   [:merge
    ChartItem
    [:map
     [:chart/type [:= ::chart/status-changes]]
     [:chart/data {:optional true} SeriesCategories]]]
   ChartDataOrWarning])
